<script setup>
import {ref, computed} from 'vue';
import DirectoryLogosRow from './DirectoryLogosRow.vue';
import {getCitations} from "@/composables/citations/useCitations";

const {data, isLoading} = getCitations();

const isPaused = ref(false);
const directories = computed(() => data.value?.all_directories);

const directoryCount = computed(() => directories.value?.length || 0);

const shouldShowOneRow = computed(() => directoryCount.value > 0 && directoryCount.value < 18);
const shouldShowTwoRows = computed(() => directoryCount.value >= 18);
const shouldAnimate = computed(() => directoryCount.value > 8);

const firstRowDirectories = computed(() => {
  if (!directories.value?.length) {
    return [];
  }

  // if we want to show 2 rows split in half
  if (shouldShowTwoRows.value) {
    const half = Math.ceil(directories.value.length / 2);
    return directories.value.slice(0, half);
  }

  return directories.value;
});

const secondRowDirectories = computed(() => {
  if (!directories.value?.length || !shouldShowTwoRows.value) {
    return [];
  }

  const half = Math.ceil(directories.value.length / 2);
  return directories.value.slice(half);
});

const handleMouseEnter = () => {
  if (shouldAnimate.value) {
    isPaused.value = true;
  }
};

const handleMouseLeave = () => {
  if (shouldAnimate.value) {
    isPaused.value = false;
  }
};
</script>

<template>
  <div class="tw-relative tw-w-full tw-overflow-hidden">
    <div
      v-if="!isLoading && directories?.length"
      class="tw-space-y-1"
    >
      <DirectoryLogosRow
        v-if="shouldShowOneRow"
        :directories="firstRowDirectories"
        :animation-class="shouldAnimate ? 'scroll-left-to-right' : ''"
        key-prefix="single-row"
        :is-paused="isPaused"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
      />

      <template v-else-if="shouldShowTwoRows">
        <DirectoryLogosRow
          :directories="firstRowDirectories"
          animation-class="scroll-left-to-right"
          key-prefix="row1"
          :is-paused="isPaused"
          @mouseenter="handleMouseEnter"
          @mouseleave="handleMouseLeave"
        />

        <DirectoryLogosRow
          :directories="secondRowDirectories"
          animation-class="scroll-right-to-left"
          key-prefix="row2"
          :is-paused="isPaused"
          @mouseenter="handleMouseEnter"
          @mouseleave="handleMouseLeave"
        />
      </template>
    </div>
    <!-- side faders -->
    <div
      v-if="!isLoading && directories?.length && shouldAnimate"
      class="tw-absolute tw-top-0 tw-bottom-0 tw-left-0 tw-w-8 tw-z-10 tw-pointer-events-none tw-bg-gradient-to-r tw-from-white tw-to-white/0"
    />

    <div
      v-if="!isLoading && directories?.length && shouldAnimate"
      class="tw-absolute tw-top-0 tw-bottom-0 tw-right-0 tw-w-8 tw-z-10 tw-pointer-events-none tw-bg-gradient-to-l tw-from-white tw-to-white/0"
    />
  </div>
</template>

<style>
/* Carousel animations with proper directions */
@keyframes scroll-left-to-right {
  0% { transform: translateX(-50%); }
  100% { transform: translateX(0%); }
}

@keyframes scroll-right-to-left {
  0% { transform: translateX(0%); }
  100% { transform: translateX(-50%); }
}

.scroll-left-to-right {
  animation: scroll-left-to-right 110s linear infinite;
}

.scroll-right-to-left {
  animation: scroll-right-to-left 110s linear infinite;
}
</style>
